import { Button } from '../../../components/ui/button';
import { GalleryBlock, GiftsBlock, RsvpBlock, renderGalleryByPosition } from '../shared/TemplateBlocks';

const getLocationSummary = (site, locationCards) => {
  if (site.same_location && locationCards?.[0]) {
    return {
      ceremony: locationCards[0],
      reception: {
        title: 'Recepção',
        location: 'Mesmo local',
        address: locationCards[0].location || locationCards[0].address,
        time: site.reception_time || site.ceremony_time
      }
    };
  }

  return {
    ceremony: locationCards?.[0] || {
      title: 'Cerimônia',
      location: site.ceremony_location,
      address: site.ceremony_address,
      time: site.ceremony_time
    },
    reception: locationCards?.[1] || {
      title: 'Recepção',
      location: site.reception_location,
      address: site.reception_address,
      time: site.reception_time
    }
  };
};

export default function VintageRetroTemplate(props) {
  const {
    site,
    theme,
    weddingDateLabel,
    daysUntil,
    locationCards,
    availableGifts,
    galleryPosition,
    isTemplatePreview,
    onTemplatePreviewRedirect,
    onOpenRsvp,
    onGiftClick,
    onOpenGallery,
    showCountdown,
    showGiftSection,
    showRsvpCount,
    confirmedGuests
  } = props;

  const primary = theme.primaryColor || '#704214';
  const secondary = theme.secondaryColor || '#C4922A';
  const paper = theme.bannerSectionBackground || '#FAF5EA';
  const cream = theme.gallerySectionBackground || '#F5EDD8';
  const titleColor = theme.heroTitleColor || primary;
  const bodyColor = theme.bannerTextColor || theme.bodyTextColor || primary;
  const { ceremony, reception } = getLocationSummary(site, locationCards);

  const galleryNode = site.gallery_images?.length ? (
    <section className="grid grid-cols-2 md:grid-cols-4" style={{ background: theme.gallerySectionBackground || cream }}>
      {site.gallery_images.slice(0, 8).map((img, idx) => (
        <button
          key={idx}
          type="button"
          onClick={() => onOpenGallery?.(idx)}
          className="group h-44 overflow-hidden md:h-72"
          aria-label={`Abrir foto ${idx + 1}`}
        >
          <img
            src={img}
            alt={`Gallery ${idx + 1}`}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          />
        </button>
      ))}
    </section>
  ) : (
    <GalleryBlock
      galleryImages={site.gallery_images}
      headingFont={theme.headingFont}
      titleColor={theme.galleryTitleColor || primary}
      secondaryColor={theme.galleryIconColor || secondary}
      sectionBackground={theme.gallerySectionBackground || cream}
      onOpenGallery={onOpenGallery}
      cardClass="rounded-none"
    />
  );

  return (
    <>
      <section
        className="relative flex min-h-[86vh] items-center justify-center overflow-hidden px-6 py-20 text-center"
        style={{
          background: `radial-gradient(circle at 50% 44%, rgba(255,255,255,0.12), transparent 38%), linear-gradient(135deg, ${primary}, ${cream})`
        }}
      >
        <div className="relative z-10 max-w-5xl">
          <p
            className="mb-8 text-[0.68rem] uppercase tracking-[0.46em]"
            style={{ color: secondary, fontFamily: theme.bannerTextFont }}
          >
            Virei Noiva · Casamento
          </p>
          <h1
            className="text-5xl italic leading-[0.95] md:text-7xl lg:text-8xl"
            style={{ color: titleColor, fontFamily: theme.bannerTitleFont || theme.headingFont }}
          >
            {site.couple_names}
          </h1>
          <div className="mx-auto my-8 h-px w-16" style={{ background: secondary }} />
          <p
            className="mx-auto max-w-2xl text-xs uppercase tracking-[0.22em] md:text-sm"
            style={{ color: bodyColor, fontFamily: theme.bannerTextFont }}
          >
            {weddingDateLabel}
          </p>
          {showCountdown ? (
            <p className="mt-6 text-sm uppercase tracking-[0.28em]" style={{ color: secondary, fontFamily: theme.bannerTextFont }}>
              {daysUntil} dias para celebrar
            </p>
          ) : null}
          <Button
            onClick={() => (isTemplatePreview ? onTemplatePreviewRedirect?.() : onOpenRsvp?.())}
            className="mt-10 rounded-none px-10 uppercase tracking-[0.26em]"
            style={{
              background: theme.confirmButtonBackground || primary,
              color: theme.confirmButtonText || cream,
              borderColor: theme.confirmButtonBorder || primary,
              borderWidth: 1,
              borderStyle: 'solid'
            }}
          >
            Confirmar Presença
          </Button>
        </div>
      </section>

      {renderGalleryByPosition(galleryPosition, 'after_hero', galleryNode)}

      <section className="px-6 py-20" style={{ background: theme.storySectionBackground || paper }}>
        <div className="mx-auto grid max-w-5xl items-center gap-12 md:grid-cols-[0.9fr_1.1fr]">
          <div className="min-h-[420px] overflow-hidden" style={{ background: `linear-gradient(135deg, ${cream}, ${secondary})` }}>
            {theme.heroImage ? (
              <img src={theme.heroImage} alt={site.couple_names} className="h-full min-h-[420px] w-full object-cover" />
            ) : (
              <div className="flex h-full min-h-[420px] items-center justify-center text-xs uppercase tracking-[0.24em]" style={{ color: primary }}>
                Foto do casal
              </div>
            )}
          </div>
          <div>
            <p className="mb-5 text-[0.65rem] uppercase tracking-[0.45em]" style={{ color: secondary, fontFamily: theme.storyBodyFont }}>
              Nossa história
            </p>
            <h2
              className="mb-6 text-3xl italic leading-tight md:text-5xl"
              style={{ color: theme.storyTitleColor || primary, fontFamily: theme.storyTitleFont || theme.headingFont }}
            >
              Como tudo começou
            </h2>
            <p
              className="whitespace-pre-line text-sm leading-8 md:text-base"
              style={{ color: theme.storyBodyColor || bodyColor, fontFamily: theme.storyBodyFont }}
            >
              {site.story_text}
            </p>
          </div>
        </div>
      </section>

      {renderGalleryByPosition(galleryPosition, 'after_story', galleryNode)}

      <section className="px-6 py-20" style={{ background: theme.locationSectionBackground || primary }}>
        <h2
          className="mb-12 text-center text-4xl italic"
          style={{ color: theme.locationTitleColor || cream, fontFamily: theme.locationTitleFont || theme.headingFont }}
        >
          O grande dia
        </h2>
        <div className="mx-auto grid max-w-5xl gap-px md:grid-cols-3" style={{ background: 'rgba(255,255,255,0.14)' }}>
          {[
            {
              label: 'Cerimônia',
              value: ceremony?.location || ceremony?.title || 'Local da cerimônia',
              sub: [ceremony?.time, ceremony?.address].filter(Boolean).join(' · ')
            },
            {
              label: 'Recepção',
              value: reception?.location || reception?.title || 'Local da recepção',
              sub: [reception?.time, reception?.address].filter(Boolean).join(' · ')
            },
            {
              label: showRsvpCount ? 'Confirmados' : 'Contagem',
              value: showRsvpCount ? `${confirmedGuests || 0} pessoa(s)` : `${daysUntil} dias`,
              sub: showRsvpCount ? 'Já confirmaram presença' : 'Até o grande dia'
            }
          ].map((item) => (
            <div
              key={item.label}
              className="p-8 text-center"
              style={{ background: theme.locationCardBackground || 'rgba(255,255,255,0.05)' }}
            >
              <p className="mb-4 text-[0.62rem] uppercase tracking-[0.38em]" style={{ color: theme.locationIconColor || secondary }}>
                {item.label}
              </p>
              <p
                className="mb-2 text-2xl italic"
                style={{ color: theme.locationTitleColor || cream, fontFamily: theme.locationTitleFont || theme.headingFont }}
              >
                {item.value}
              </p>
              <p className="text-xs leading-6" style={{ color: theme.locationTextColor || 'rgba(245,237,216,0.68)', fontFamily: theme.locationTextFont }}>
                {item.sub}
              </p>
            </div>
          ))}
        </div>
      </section>

      {renderGalleryByPosition(galleryPosition, 'before_gifts', galleryNode)}

      <GiftsBlock
        gifts={availableGifts}
        headingFont={theme.headingFont}
        theme={theme}
        titleColor={primary}
        textColor={theme.bodyTextColor}
        secondaryColor={secondary}
        onGiftClick={onGiftClick}
        compact
        showGiftSection={showGiftSection}
      />

      {renderGalleryByPosition(galleryPosition, 'end', galleryNode)}

      <RsvpBlock
        onOpenRsvp={onOpenRsvp}
        isTemplatePreview={isTemplatePreview}
        onTemplatePreviewRedirect={onTemplatePreviewRedirect}
        primaryColor={primary}
        secondaryColor={secondary}
        sectionBackground={theme.rsvpSectionBackground || cream}
        buttonBackground={theme.confirmButtonBackground || primary}
        buttonText={theme.confirmButtonText || cream}
        buttonBorder={theme.confirmButtonBorder || primary}
        iconColor={theme.rsvpIconColor || secondary}
        titleColor={theme.rsvpTextColor || primary}
        bodyColor={theme.rsvpTextColor || bodyColor}
        countColor={theme.rsvpTextColor || bodyColor}
        headingFont={theme.rsvpTitleFont || theme.headingFont}
        bodyFont={theme.rsvpTextFont}
        showRsvpCount={showRsvpCount}
        confirmedGuests={confirmedGuests}
      />

      <footer className="px-6 py-12 text-center" style={{ background: primary }}>
        <div className="text-3xl italic" style={{ color: cream, fontFamily: theme.headingFont }}>
          {site.couple_names}
        </div>
        <div className="mt-2 text-[0.65rem] uppercase tracking-[0.35em]" style={{ color: 'rgba(245,237,216,0.55)', fontFamily: theme.bodyFont }}>
          Virei Noiva
        </div>
      </footer>
    </>
  );
}

